// src/components/AlbumView.tsx
import React, { useEffect, useRef, useState } from "react";
import type { Folder, FileItem } from "../types/types";
import { useNavigate } from "react-router-dom";
import FolderNameModal from "./FolderNameModal";
import { apiService, VoiceoverStatusResponse } from "../services/api";
import { getUploadPhase } from "../utils/uploadPhase";
import VerifyOriginalModal from "./VerifyOriginalModal";
import SupportTicketModal from "./SupportTicketModal";
import { VALIDATION_FAILED_TITLE, VALIDATION_FAILED_REASONS, VALIDATION_FAILED_HELP } from "../constants/validationCopy";

function formatBytes(bytes?: number): string {
  if (!bytes || bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))}${sizes[i]}`;
}

function formatDuration(seconds?: number): string {
  if (!seconds) return "0:00";
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

type Props = {
  folder: Folder;
  showHeader?: boolean;
  onBack?: () => void;
  onAddFiles?: (files: FileList | null) => void;
  onDeleteFolder?: () => void;
  onRenameFolder?: ((name: string) => any) | (() => any);
  onRemoveFile?: (fileId: string) => void;
  allFolders?: Folder[];
  onMoveFiles?: (sourceId: string, targetId: string, fileIds: string[]) => any;
  onMoveAllFiles?: (sourceId: string, targetId: string) => any;
  onOpenFolder?: (id: string) => void;
  breadcrumb?: { id: string; name: string }[];
  showFileContextMenu?: boolean;
  onCheckVoiceoverStatus?: (mediaId: string) => Promise<VoiceoverStatusResponse>;
};

export default function AlbumView({
  folder,
  showHeader = true,
  onBack,
  onAddFiles,
  onDeleteFolder,
  onRenameFolder,
  onRemoveFile,
  allFolders = [],
  onMoveFiles,
  onMoveAllFiles,
  onOpenFolder,
  breadcrumb = [],
  showFileContextMenu = true,
  onCheckVoiceoverStatus,
}: Props) {

  function sortByNewest(list: FileItem[]): FileItem[] {
  return [...list].sort((a, b) => Number(b.createdAt ?? 0) - Number(a.createdAt ?? 0));
}

  const fileInputRef = useRef<HTMLInputElement | null>(null);
const [files, setFiles] = useState<FileItem[]>(sortByNewest(folder.files || []));
  const [selectedFileIds, setSelectedFileIds] = useState<Set<string>>(new Set());
  const [selectedMoveTarget, setSelectedMoveTarget] = useState<string>("");
  const [selectedMoveAllTarget, setSelectedMoveAllTarget] = useState<string>("");
  
  // PAGINATION STATE
  const [visibleCount, setVisibleCount] = useState<number>(8);

  const navigate = useNavigate();
  const [contextMenu, setContextMenu] = useState<{ fileId: string; x: number; y: number } | null>(null);
  const [mobileMenuFileId, setMobileMenuFileId] = useState<string | null>(null);

  const [detailsFile, setDetailsFile] = useState<FileItem | null>(null);
  const [downloading, setDownloading] = useState(false);
  // Recovery flow for a rejected media (verify-with-original), matching iOS.
  const [verifyOpen, setVerifyOpen] = useState(false);
  const [verifyNoOriginal, setVerifyNoOriginal] = useState(false);
  const [supportOpen, setSupportOpen] = useState(false);
  const [supportPreset, setSupportPreset] = useState<{ slug: string; subject: string; message: string } | null>(null);
  const [voiceoverStatusLoading, setVoiceoverStatusLoading] = useState<boolean>(false);
  const [voiceoverStatusData, setVoiceoverStatusData] = useState<any>(null);
  const [hasCheckedVoiceover, setHasCheckedVoiceover] = useState<boolean>(false);
  const [isRefreshingVoiceover, setIsRefreshingVoiceover] = useState<boolean>(false);

  const [folderModal, setFolderModal] = useState<
    | {
        open: true;
        mode?: "create" | "rename";
        title?: string;
        initialValue?: string;
        context: { type: "rename-folder" } | { type: "create-subfolder" } | { type: "rename-file-label"; fileId: string };
      }
    | null
  >(null);

  const [lastApiCall, setLastApiCall] = useState<null | { name: string; payload: any; time: string; result?: any }>(null);

  const [isMovingSelected, setIsMovingSelected] = useState(false);
  const [isMovingAll, setIsMovingAll] = useState(false);

  const [isMobile, setIsMobile] = useState<boolean>(typeof window !== "undefined" ? window.innerWidth < 640 : false);
  
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Prevent background scrolling when any modal is open
  useEffect(() => {
    if (detailsFile || folderModal || verifyOpen || supportOpen || mobileMenuFileId) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [detailsFile, folderModal, verifyOpen, supportOpen, mobileMenuFileId]);

  const cardWrapperRef = useRef<HTMLDivElement | null>(null);

  const touchTimerRef = useRef<number | null>(null);
  function handleTouchStartForLongPress(e: React.TouchEvent, fileId: string) {
    if (!e.touches || e.touches.length === 0) return;
    touchTimerRef.current = window.setTimeout(() => {
      setMobileMenuFileId(fileId);
    }, 600);
  }
  function handleTouchEndForLongPress() {
    if (touchTimerRef.current) {
      clearTimeout(touchTimerRef.current);
      touchTimerRef.current = null;
    }
  }

  useEffect(() => {
    console.debug("[AlbumView] mounted - folder:", folder?.id, folder?.name);
  }, []);

 // after
useEffect(() => {
  setFiles(sortByNewest(folder.files || []));
  setSelectedFileIds(new Set());
  setVisibleCount(8); // Reset pagination when folder changes
}, [folder.id, folder.files]);

  useEffect(() => {
    if (!contextMenu) return;
    const handleGlobal = () => setContextMenu(null);
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setContextMenu(null);
    };
    window.addEventListener("click", handleGlobal);
    window.addEventListener("keydown", handleKey);
    return () => {
      window.removeEventListener("click", handleGlobal);
      window.removeEventListener("keydown", handleKey);
    };
  }, [contextMenu]);

  // Voiceover status checking effect
  useEffect(() => {
    let mounted = true;

    const checkVoiceoverStatus = async () => {
      if (!detailsFile || !detailsFile.id) return;
      
      // Only check for audio files that might have voiceover
      const isAudioFile = detailsFile.isAudio || detailsFile.type?.startsWith('audio/');
      if (!isAudioFile) return;

      // Don't check if we already checked voiceover status for this file
      if (hasCheckedVoiceover && detailsFile.id === detailsFile.id) return;

      // Don't check if we already have voiceover data from the file object
      if (detailsFile.voiceoverUrl !== undefined || 
          detailsFile.voiceoverGenerated !== undefined ||
          detailsFile.voiceoverPending !== undefined) {
        return;
      }

      setVoiceoverStatusLoading(true);
      try {
        console.log(`🎙️ Checking voiceover status for: ${detailsFile.id}`);
        
        let response: VoiceoverStatusResponse;
        
        if (onCheckVoiceoverStatus) {
          response = await onCheckVoiceoverStatus(detailsFile.id);
        } else {
          response = await apiService.checkVoiceoverStatus(detailsFile.id);
        }
        
        if (mounted && response.success && response.data) {
          console.log(`✅ Vastavik Voice ID status: generated=${response.data.voiceoverGenerated}, pending=${response.data.voiceoverPending}`);
          
          setVoiceoverStatusData(response.data);
          setHasCheckedVoiceover(true);
          
          // Update the file with voiceover data
          setDetailsFile(prev => prev ? {
            ...prev,
            voiceoverUrl: response.data?.voiceoverUrl,
            voiceoverGenerated: response.data?.voiceoverGenerated,
            voiceoverPending: response.data?.voiceoverPending,
            voiceoverGender: response.data?.voiceoverGender,
            voiceoverDuration: response.data?.voiceoverDuration,
            voiceoverError: response.data?.voiceoverError,
            nftTokenId: response.data?.nftTokenId,
          } : null);
        } else if (mounted && response.error) {
          console.error(`❌ Vastavik Voice ID status check failed: ${response.error}`);
        }
      } catch (error) {
        console.error("Error checking voiceover status:", error);
      } finally {
        if (mounted) {
          setVoiceoverStatusLoading(false);
          setIsRefreshingVoiceover(false);
        }
      }
    };

    // Use a timeout to prevent immediate check on modal open
    const timer = setTimeout(() => {
      checkVoiceoverStatus();
    }, 500);

    return () => {
      mounted = false;
      clearTimeout(timer);
    };
  }, [detailsFile?.id]);

  // Function to manually trigger voiceover status check
  const handleCheckVoiceoverStatus = async () => {
    if (!detailsFile?.id) return;
    
    setIsRefreshingVoiceover(true);
    setVoiceoverStatusLoading(true);
    try {
      let response: VoiceoverStatusResponse;
      
      if (onCheckVoiceoverStatus) {
        response = await onCheckVoiceoverStatus(detailsFile.id);
      } else {
        response = await apiService.checkVoiceoverStatus(detailsFile.id);
      }
      
      if (response.success && response.data) {
        setVoiceoverStatusData(response.data);
        setHasCheckedVoiceover(true);
        
        // Update the file with voiceover data
        setDetailsFile(prev => prev ? {
          ...prev,
          voiceoverUrl: response.data?.voiceoverUrl,
          voiceoverGenerated: response.data?.voiceoverGenerated,
          voiceoverPending: response.data?.voiceoverPending,
          voiceoverGender: response.data?.voiceoverGender,
          voiceoverDuration: response.data?.voiceoverDuration,
          voiceoverError: response.data?.voiceoverError,
          nftTokenId: response.data?.nftTokenId,
        } : null);
      } else if (response.error) {
        alert(`Voiceover status check failed: ${response.error}`);
      }
    } catch (error: any) {
      alert(`Error checking voiceover status: ${error.message || "Unknown error"}`);
    } finally {
      setVoiceoverStatusLoading(false);
      setIsRefreshingVoiceover(false);
    }
  };

  // Function to share voiceover audio
  const handleShareVoiceover = async () => {
    if (!detailsFile?.voiceoverUrl) {
      alert("No voiceover URL available to share.");
      return;
    }
    
    try {
      if (navigator.share) {
        await navigator.share({
          title: `${detailsFile.name}`,
          text: `Check out this: ${detailsFile.name}`,
          url: detailsFile.voiceoverUrl,
        });
      } else {
        await navigator.clipboard.writeText(detailsFile.voiceoverUrl);
        alert("URL copied to clipboard!");
      }
    } catch (error: any) {
      if (error.name !== 'AbortError') {
        try {
          await navigator.clipboard.writeText(detailsFile.voiceoverUrl);
          alert("URL copied to clipboard!");
        } catch (clipboardError) {
          alert("Could not share or copy the voiceover URL.");
        }
      }
    }
  };

  const formatDateUK = (dateString: any) => {
    if (!dateString) return "—";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "—";
      
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      
      return `${day}/${month}/${year}, ${hours}:${minutes}`;
    } catch (error) {
      return "—";
    }
  };

  function toggleSelect(fileId: string) {
    setSelectedFileIds((prev) => {
      const next = new Set(prev);
      if (next.has(fileId)) next.delete(fileId);
      else next.add(fileId);
      return next;
    });
  }

  function getFileById(id: string) {
    return files.find((f) => f.id === id);
  }

  function getLabelForFile(fileId: string) {
    const f = getFileById(fileId);
    if (!f) return "";
    return f.displayName && f.displayName !== f.name ? f.displayName : f.name;
  }

  function openFile(fileId: string) {
    const f = getFileById(fileId);
    if (!f?.url) return;
    window.open(f.url, "_blank");
  }

  async function downloadUrl(url: string, filename: string) {
    setDownloading(true);
    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = filename;
      a.click();
      setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    } catch {
      window.open(url, "_blank");
    } finally {
      setDownloading(false);
    }
  }

  function downloadFile(fileId: string) {
    const f = getFileById(fileId);
    if (!f?.url) return;
    void downloadUrl(f.url, f.name || "vastavik-media");
  }

  async function copyFileLink(fileId: string) {
    const f = getFileById(fileId);
    if (!f?.url) {
      alert("No URL to copy for this file.");
      return;
    }
    try {
      await navigator.clipboard.writeText(f.url);
      alert("Link copied to clipboard");
    } catch {
      alert("Could not copy link");
    }
  }

  function parentWantsToOpenModal(): boolean {
    if (!onRenameFolder) return false;
    try {
      return (onRenameFolder as any).length === 0;
    } catch {
      return false;
    }
  }

  function handleClickRenameFolderButton() {
    if (onRenameFolder && parentWantsToOpenModal()) {
      try {
        (onRenameFolder as () => any)();
      } catch (err) {
        // swallow
      }
      return;
    }

    setFolderModal({
      open: true,
      mode: "rename",
      title: "Rename folder",
      initialValue: folder.name,
      context: { type: "rename-folder" },
    });
  }

  function renameFileLabel(fileId: string) {
    const f = getFileById(fileId);
    if (!f) return;
    setFolderModal({
      open: true,
      mode: "rename",
      title: "Rename display name",
      initialValue: f.displayName || f.name,
      context: { type: "rename-file-label", fileId },
    });
  }

  function showFileDetails(fileId: string) {
    const f = getFileById(fileId);
    if (!f) return;
    setHasCheckedVoiceover(false);
    setVoiceoverStatusData(null);
    setVoiceoverStatusLoading(false);
    setIsRefreshingVoiceover(false);
    setDetailsFile(f);
  }

  function openContextMenuAtEvent(e: React.MouseEvent, fileId: string) {
    e.preventDefault();
    e.stopPropagation();
    let x = e.clientX;
    let y = e.clientY;
    const menuW = 190;
    const menuH = 260;
    const padding = 8;
    const bottomReserve = 80;
    if (typeof window !== "undefined") {
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      if (x + menuW + padding > vw) x = vw - menuW - padding;
      if (x < padding) x = padding;
      const maxBottom = vh - bottomReserve;
      if (y + menuH > maxBottom) y = maxBottom - menuH;
      if (y < padding) y = padding;
    }
    setContextMenu({ fileId, x, y });
  }

  async function handleMoveSelected() {
    if (isMovingSelected) return;
    if (!selectedMoveTarget) return alert("Choose a destination folder first");
    const ids = Array.from(selectedFileIds);
    const payload = { sourceId: folder.id, targetId: selectedMoveTarget, fileIds: ids };
    setLastApiCall({ name: "moveSelected", payload, time: new Date().toISOString() });

    setIsMovingSelected(true);
    try {
      if (!onMoveFiles) {
        setFiles((prev) => prev.filter((f) => !ids.includes(f.id)));
        setSelectedFileIds(new Set());
        setSelectedMoveTarget("");
        return;
      }

      const res = onMoveFiles(folder.id, selectedMoveTarget, ids);
      if (res && typeof (res as Promise<any>).then === "function") {
        const awaited = await (res as Promise<any>);
        setLastApiCall((s) => (s ? { ...s, result: awaited } : null));
      } else {
        setLastApiCall((s) => (s ? { ...s, result: res } : null));
      }
      setFiles((prev) => prev.filter((f) => !ids.includes(f.id)));
    } catch (err) {
      alert("Move failed: " + (err instanceof Error ? err.message : String(err)));
    } finally {
      setIsMovingSelected(false);
      setSelectedMoveTarget("");
      setSelectedFileIds(new Set());
    }
  }

  async function handleMoveAll() {
    if (isMovingAll) return;
    if (!selectedMoveAllTarget) return alert("Choose a destination folder first");
    setLastApiCall({ name: "moveAll", payload: { sourceId: folder.id, targetId: selectedMoveAllTarget }, time: new Date().toISOString() });

    setIsMovingAll(true);
    try {
      if (!onMoveAllFiles) {
        setFiles([]);
        setSelectedMoveAllTarget("");
        return;
      }

      const res = onMoveAllFiles(folder.id, selectedMoveAllTarget);
      if (res && typeof (res as Promise<any>).then === "function") {
        const awaited = await (res as Promise<any>);
        setLastApiCall((s) => (s ? { ...s, result: awaited } : null));
      } else {
        setLastApiCall((s) => (s ? { ...s, result: res } : null));
      }
      setFiles([]);
    } catch (err) {
      alert("Move all failed: " + (err instanceof Error ? err.message : String(err)));
    } finally {
      setIsMovingAll(false);
      setSelectedMoveAllTarget("");
    }
  }

  const Spinner = ({ className = "h-4 w-4 mr-2 inline-block align-middle" }: { className?: string }) => (
    <svg className={`${className} animate-spin`} viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="12" cy="12" r="10" stroke="rgba(255,255,255,0.15)" strokeWidth="4" />
      <path d="M22 12a10 10 0 00-10-10" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
    </svg>
  );

  return (
    <div className="w-full text-black dark:text-white bg-transparent" onClick={() => { setContextMenu(null); setMobileMenuFileId(null); }}>

      {/* Top Control Bar */}
      <div className="px-5 py-4 border-b border-black dark:border-white flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-transparent">
        <div className="text-[13px] text-gray-500 dark:text-[#848484] flex items-center gap-2 font-['Product_Sans']">
          <span className="w-[18px] h-[18px] rounded-full border border-gray-400 dark:border-[#848484] flex items-center justify-center text-[10px]">i</span>
          Use Ctrl/⌘ + Click or checkboxes to select files.
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          <select
            className="h-10 rounded-lg border border-black dark:border-white bg-transparent px-3 text-[13px] text-black dark:text-white focus:outline-none focus:border-gray-400 dark:focus:border-white font-['Product_Sans'] font-medium"
            value={selectedFileIds.size > 0 ? selectedMoveTarget : selectedMoveAllTarget}
            onChange={(e) => selectedFileIds.size > 0 ? setSelectedMoveTarget(e.target.value) : setSelectedMoveAllTarget(e.target.value)}
            disabled={isMovingAll || isMovingSelected}
          >
            <option value="" className="dark:bg-[#141414]">{selectedFileIds.size > 0 ? "Move selected to…" : "Move ALL to…"}</option>
            {allFolders.filter((f) => f.id !== folder.id).map((f) => (
              <option key={f.id} value={f.id} className="dark:bg-[#141414]">{f.name}</option>
            ))}
          </select>

          <button
            className="h-10 rounded-lg border border-black dark:border-white text-black dark:text-black bg-white dark:bg-white px-5 text-[13px] font-bold flex items-center font-['Product_Sans'] hover:bg-gray-100 dark:hover:bg-gray-200 transition-colors"
            onClick={selectedFileIds.size > 0 ? handleMoveSelected : handleMoveAll}
            disabled={isMovingAll || isMovingSelected}
          >
            {isMovingAll || isMovingSelected ? (<><Spinner /> Moving…</>) : selectedFileIds.size > 0 ? "Move Selected" : "Move ALL"}
          </button>
        </div>
      </div>

      <div className="p-5 md:p-6 bg-transparent">
        {files.length === 0 ? (
          <div className="py-12 text-center text-sm text-gray-500 font-['Product_Sans']">
            No items here.
          </div>
        ) : (
          <>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {/* SLICED ARRAY FOR PAGINATION */}
              {files.slice(0, visibleCount).map((file) => {
                const phase = getUploadPhase(file);
                const isSelected = selectedFileIds.has(file.id);

                return (
                  <div
                    key={file.id}
                    data-file-card
                    className={`
                      relative
                      flex
                      flex-col
                      cursor-pointer
                      rounded-lg
                      bg-gray-50
                      dark:bg-[#141414]
                      border
                      overflow-hidden
                      hover:border-gray-400
                      dark:hover:border-gray-500
                      transition-colors
                      shadow-sm
                      dark:shadow-none
                      h-full
                      ${
                        isSelected
                          ? "border-blue-500 dark:border-white"
                          : "border-black dark:border-white"
                      }
                    `}
                    onClick={(e) => {
                      const me = e as React.MouseEvent;

                      setContextMenu(null);

                      if (me.ctrlKey || me.metaKey) {
                        toggleSelect(file.id);
                        return;
                      }

                      if (selectedFileIds.size > 0) {
                        toggleSelect(file.id);
                        return;
                      }

                      showFileDetails(file.id);
                    }}
                    onContextMenu={(e) =>
                      !isMobile && openContextMenuAtEvent(e, file.id)
                    }
                    onTouchStart={(e) => {
                      if (isMobile) {
                        handleTouchStartForLongPress(e, file.id);
                      }
                    }}
                    onTouchEnd={() => {
                      if (isMobile) {
                        handleTouchEndForLongPress();
                      }
                    }}
                    draggable={!isMobile}
                    onDragStart={(e) => {
                      e.dataTransfer.setData(
                        "application/json",
                        JSON.stringify({
                          fileId: file.id,
                          sourceId: folder.id,
                        })
                      );

                      e.dataTransfer.effectAllowed = "move";
                    }}
                  >
                    {/* CHECKBOX */}
                    <label
                      className="absolute top-3 left-3 z-20 cursor-pointer"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <input
                        type="checkbox"
                        className="
                          w-[17px]
                          h-[17px]
                          rounded-[3px]
                          border
                          border-white/50
                          bg-black/40
                          checked:bg-white
                          checked:border-white
                          appearance-none
                          cursor-pointer
                          flex
                          items-center
                          justify-center
                          after:content-['✓']
                          after:text-black
                          after:text-[11px]
                          after:font-bold
                          after:opacity-0
                          checked:after:opacity-100
                        "
                        checked={isSelected}
                        onChange={() => toggleSelect(file.id)}
                      />
                    </label>

                    {/* THREE DOT MENU */}
                    <button
                      type="button"
                      className="
                        absolute
                        top-3
                        right-3
                        z-20
                        flex
                        h-[26px]
                        w-[26px]
                        items-center
                        justify-center
                        rounded-full
                        bg-[#141414]/85
                        border
                        border-white/10
                        text-white
                        hover:bg-[#333]
                        backdrop-blur-md
                      "
                      onClick={(e) => {
                        e.stopPropagation();

                        if (isMobile) {
                          setMobileMenuFileId(file.id);
                        } else {
                          openContextMenuAtEvent(e as any, file.id);
                        }
                      }}
                    >
                      <svg
                        width="13"
                        height="13"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                      >
                        <circle cx="12" cy="12" r="2" />
                        <circle cx="19" cy="12" r="2" />
                        <circle cx="5" cy="12" r="2" />
                      </svg>
                    </button>

                    {/* IMAGE / MEDIA */}
                    <div
                      className="
                        relative
                        w-full
                        h-[140px]
                        shrink-0
                        bg-gray-200
                        dark:bg-[#222]
                        flex
                        items-center
                        justify-center
                        overflow-hidden
                        border-b
                        border-gray-200
                        dark:border-[#333]
                      "
                    >
                                    {file.type?.startsWith("image/") && file.url ? (
                        <img
                          src={file.url}
                          alt={file.name}
                          className={`
                            w-full
                            h-full
                            object-cover
                            ${
                              phase.phase === "processing" || phase.phase === "verifying"
                                ? "opacity-60"
                                : "opacity-100"
                            }
                          `}
                        />
                      ) : file.type?.startsWith("audio/") ? (
                        <svg
                          width="36"
                          height="36"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                          className="text-gray-400 dark:text-[#555]"
                        >
                          <path d="M9 18V5l12-2v13" />
                          <circle cx="6" cy="18" r="3" />
                          <circle cx="18" cy="16" r="3" />
                        </svg>
                      ) : file.type?.startsWith("video/") ? (
                        <div className="text-[36px] text-gray-400 dark:text-[#555]">
                          🎥
                        </div>
                      ) : (
                        <svg
                          width="38"
                          height="38"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="text-gray-400 dark:text-[#848484]"
                        >
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                          <polyline points="14 2 14 8 20 8" />
                          <line x1="16" y1="13" x2="8" y2="13" />
                          <line x1="16" y1="17" x2="8" y2="17" />
                          <polyline points="10 9 9 9 8 9" />
                        </svg>
                      )}

                                  {/* REJECTED */}
                      {phase.phase === "failed" && (
                        <span
                          className="
                            absolute
                            bottom-2.5
                            right-2.5
                            flex
                            items-center
                            gap-1.5
                            rounded
                            px-2
                            py-1
                            bg-red-50
                            dark:bg-[#2A1616]
                            border
                            border-red-500/20
                            text-[10px]
                            font-bold
                            text-red-500
                            font-['Product_Sans']
                            uppercase
                            tracking-wider
                          "
                        >
                          <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
                          Rejected
                        </span>
                      )}

                      {phase.phase === "processing" && (
                        <div className="absolute bottom-0 left-0 right-0 h-1 bg-black/20 dark:bg-white/10">
                          <div
                            className="h-full bg-amber-400 transition-all"
                            style={{
                              width: `${phase.progress}%`,
                            }}
                          />
                        </div>
                      )}
                    </div>

                    {/* DETAILS */}
                    <div className="p-4 flex flex-col flex-1">
                      <div className="flex-1 flex flex-col mb-4">
                        <div
                          className="text-[13px] leading-snug font-bold text-black dark:text-white font-['Product_Sans'] mb-1.5 line-clamp-2"
                          title={getLabelForFile(file.id)}
                        >
                          {getLabelForFile(file.id)}
                        </div>
                        <div className="text-[11px] text-gray-500 dark:text-[#848484] font-['Product_Sans']">
                          {formatBytes(file.size)} •{" "}
                          {file.type?.split("/")[1] || "file"}
                        </div>
                      </div>

                      {/* STATUS BLOCK */}
                      <div className="pt-3 border-t border-gray-200 dark:border-[#333] mt-auto">
                        <div className="h-[44px] flex flex-col justify-center">
                          {phase.phase === "done" && (
                            <>
                              <div className="text-[10px] text-gray-500 dark:text-[#848484] font-['Product_Sans'] mb-1">
                                Vastavik ID:
                              </div>
                              <div className="bg-gray-200 dark:bg-[#222] text-gray-700 dark:text-[#A0A0A0] text-[10px] px-2.5 py-1.5 rounded font-['Product_Sans'] truncate tracking-wider">
                                {(file as any).ownerKey ?? "—"}
                              </div>
                            </>
                          )}

                          {phase.phase === "failed" && (
                            <span className="text-[11px] font-bold text-black dark:text-white uppercase tracking-widest font-['Product_Sans']">
                              View Details
                            </span>
                          )}

                          {phase.phase === "processing" && (
                            <>
                              <div className="text-[10px] text-amber-500 font-['Product_Sans'] mb-1">
                                Status:
                              </div>
                              <div className="bg-amber-500/10 text-amber-500 text-[10px] px-2.5 py-1.5 rounded font-['Product_Sans'] truncate">
                                Certifying… {phase.progress}%
                              </div>
                            </>
                          )}

                          {phase.phase === "verifying" && (
                            <>
                              <div className="text-[10px] text-emerald-500 font-['Product_Sans'] mb-1">
                                Status:
                              </div>
                              <div className="bg-emerald-500/10 text-emerald-500 text-[10px] px-2.5 py-1.5 rounded font-['Product_Sans'] truncate">
                                Verifying original…
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* PAGINATION FOOTER */}
            <div className="mt-[28px] pt-[20px] border-t border-black dark:border-white flex items-center justify-between">
              {visibleCount < files.length ? (
                <button
                  onClick={() => setVisibleCount((prev) => Math.min(prev + 8, files.length))}
                  className="px-[16px] py-[8px] rounded-[6px] border border-black dark:border-white bg-transparent text-black dark:text-white font-['Product_Sans'] font-medium text-[13px] hover:bg-gray-100 dark:hover:bg-white/5 transition-colors cursor-pointer"
                >
                  View more
                </button>
              ) : (
                <div />
              )}
              <div className="text-gray-500 dark:text-[#848484] text-[12px] font-['Product_Sans']">
                Showing {Math.min(visibleCount, files.length)} of {files.length}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Context Menus */}
      {contextMenu && !isMobile && showFileContextMenu && (
        <div
          className="fixed z-40 w-48 max-h-[60vh] overflow-auto rounded-xl border border-gray-200 dark:border-white/10 bg-white/95 dark:bg-[#141414]/95 backdrop-blur shadow-2xl text-sm text-black dark:text-white"
          style={{ top: contextMenu.y, left: contextMenu.x }}
          onClick={(e) => e.stopPropagation()}
        >
          <button className="block w-full px-3 py-2 text-left hover:bg-gray-100 dark:hover:bg-white/10 transition-colors" onClick={() => { openFile(contextMenu.fileId); setContextMenu(null); }}>Open</button>
          <button disabled={!getFileById(contextMenu.fileId)?.url} className={`block w-full px-3 py-2 text-left transition-colors ${getFileById(contextMenu.fileId)?.url ? "hover:bg-gray-100 dark:hover:bg-white/10 cursor-pointer" : "opacity-40 cursor-not-allowed"}`} onClick={() => { if (getFileById(contextMenu.fileId)?.url) { downloadFile(contextMenu.fileId); setContextMenu(null); } }}>Download</button>
          <button className="block w-full px-3 py-2 text-left hover:bg-gray-100 dark:hover:bg-white/10 transition-colors" onClick={async () => { await copyFileLink(contextMenu.fileId); setContextMenu(null); }}>Copy link</button>
          <button className="block w-full px-3 py-2 text-left hover:bg-gray-100 dark:hover:bg-white/10 transition-colors" onClick={() => { renameFileLabel(contextMenu.fileId); setContextMenu(null); }}>Rename (display name)</button>
          <button className="block w-full px-3 py-2 text-left hover:bg-gray-100 dark:hover:bg-white/10 transition-colors" onClick={() => { showFileDetails(contextMenu.fileId); setContextMenu(null); }}>Details</button>
        </div>
      )}

      {mobileMenuFileId && isMobile && (
        <div className="fixed inset-0 z-50 flex items-end bg-black/40" onClick={() => setMobileMenuFileId(null)}>
          <div className="w-full rounded-t-2xl bg-[#141414] text-white border-t border-white/10 p-4 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="relative flex items-center justify-between mb-3">
              <div className="absolute left-1/2 top-1 -translate-x-1/2 w-12 h-1.5 bg-white/20 rounded-full" />
              <div className="font-medium text-sm">File actions</div>
              <button onClick={() => setMobileMenuFileId(null)} className="h-8 w-8 flex items-center justify-center rounded-full hover:bg-white/10 text-gray-300">✕</button>
            </div>
            <div className="flex flex-col divide-y divide-white/10">
              <button className="py-3 px-2 rounded-md text-left hover:bg-white/10 transition-colors" onClick={() => { openFile(mobileMenuFileId); setMobileMenuFileId(null); }}>Open</button>
              <button className={`py-3 px-2 rounded-md text-left transition-colors ${getFileById(mobileMenuFileId)?.url ? "hover:bg-white/10" : "text-white/40 cursor-not-allowed"}`} onClick={() => { if (getFileById(mobileMenuFileId)?.url) { downloadFile(mobileMenuFileId); setMobileMenuFileId(null); } }}>Download</button>
              <button className="py-3 px-2 rounded-md text-left hover:bg-white/10 transition-colors" onClick={async () => { await copyFileLink(mobileMenuFileId); setMobileMenuFileId(null); }}>Copy link</button>
              <button className="py-3 px-2 rounded-md text-left hover:bg-white/10 transition-colors" onClick={() => { renameFileLabel(mobileMenuFileId); setMobileMenuFileId(null); }}>Rename</button>
              <button className="py-3 px-2 rounded-md text-left hover:bg-white/10 transition-colors" onClick={() => { showFileDetails(mobileMenuFileId); setMobileMenuFileId(null); }}>Details</button>
            </div>
          </div>
        </div>
      )}

{detailsFile && (() => {
        const f = detailsFile as any;
        const fileName = f.displayName || f.name || f.originalFilename || "media";
        
        // Define missing variables locally to fix TypeScript TS2304 errors
        const hasVastavikId = Boolean(f.ownerKey ?? f.meta?.ownerKey);
        const protType = String(f.protectionType ?? f.meta?.protectionType ?? "").toLowerCase();
        const isQrProtected =
          protType === "qr" || protType === "qr_watermark" || protType === "qrcode"
            ? true
            : protType === "vastavik_id" || protType === "ownerkey" || protType === "owner_key"
            ? false
            : Boolean(f.qrEmbedded ?? f.meta?.qrEmbedded);

        // Self-contained download handler that avoids missing `assetLoading` hook
        const downloadAssetLocal = async (kind: "qr" | "badge", e: React.MouseEvent<HTMLButtonElement>) => {
          const btn = e.currentTarget;
          const originalText = btn.innerText;
          btn.disabled = true;
          btn.innerText = "Downloading...";
          
          const mediaId = String(f.mediaId ?? f.meta?.mediaId ?? f.id ?? "");
          const ownerKey = f.ownerKey ?? f.meta?.ownerKey ?? "vastavik";
          
          if (!mediaId) {
            btn.disabled = false;
            btn.innerText = originalText;
            return;
          }
          
          try {
            const blob = kind === "qr" 
              ? await apiService.getMediaQrBlob(mediaId) 
              : await apiService.getMediaBadgeBlob(mediaId);
              
            const objectUrl = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = objectUrl;
            a.download = `vastavik-${kind}-${ownerKey}.png`;
            a.click();
            setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
          } catch (err) {
            console.error(`Failed to download ${kind}:`, err);
            alert(`Failed to download ${kind}`);
          } finally {
            btn.disabled = false;
            btn.innerText = originalText;
          }
        };
        
        // Reusable action buttons logic block
        const renderActionButtons = () => {
          const phase = getUploadPhase(f);
          const backendCanVerify = f.canVerifyWithOriginal ?? f.meta?.canVerifyWithOriginal;
          const rejectionCode = String(f.rejectionCode ?? f.meta?.rejectionCode ?? "").toUpperCase();
          const rejectionReason = f.rejectionReason ?? f.meta?.rejectionReason;
          const TERMINAL_REJECTION_CODES = ["SCREENSHOT", "SCREEN RECORDING", "DEEPFAKE", "DEEPFAKE_CHECK_UNAVAILABLE", "DEEPFAKE_UNSUPPORTED_FORMAT", "DEEPFAKE_CHECK_FAILED", "DUPLICATE", "ALREADY_REGISTERED", "ALREADY REGISTERED"];
          const terminalRejection = backendCanVerify != null
            ? !backendCanVerify
            : rejectionCode
              ? TERMINAL_REJECTION_CODES.includes(rejectionCode)
              : /screenshot|screen recording|deepfake|duplicate|uploaded before|already been|already registered/i.test(String(rejectionReason || ""));
          const mediaKind = String(f.type || "").startsWith("video/") ? "video" : String(f.type || "").startsWith("audio/") ? "audio" : "image";
          const mediaName = f.displayName || f.name || f.originalFilename || "media";
          const canRecover = phase.phase === "failed" && !f.matchVerification && !terminalRejection && /^(image|video|audio)\//.test(String(f.type || ""));

          return (
            <>
              {canRecover && (
                <>
                  <button
                    className="w-full py-3 rounded-md border border-black dark:border-[#8F8F8F] bg-transparent text-black dark:text-white text-[12px] sm:text-[13px] font-['Product_Sans'] hover:bg-gray-50 dark:hover:bg-white/5 transition"
                    onClick={() => { setVerifyNoOriginal(false); setVerifyOpen(true); }}
                  >
                    {mediaKind === "video" ? "Validate with original — upload the unedited video" : mediaKind === "audio" ? "Validate with original — upload the unedited audio" : "Validate with original — upload the unedited photo"}
                  </button>
                  <button
                    className="w-full py-3 rounded-md border border-black dark:border-[#8F8F8F] bg-transparent text-black dark:text-white text-[12px] sm:text-[13px] font-['Product_Sans'] hover:bg-gray-50 dark:hover:bg-white/5 transition"
                    onClick={() => { setSupportPreset({ slug: "missing_original", subject: `No original available: ${mediaName}`, message: `I don't have the original for my upload "${mediaName}".\n\nHere's some context: ` }); setSupportOpen(true); }}
                  >
                    I don't have the original
                  </button>
                </>
              )}
              {phase.phase === "failed" && (
                <button
                  className="w-full py-3 rounded-md border border-black dark:border-[#8F8F8F] bg-transparent text-black dark:text-white text-[12px] sm:text-[13px] font-['Product_Sans'] hover:bg-gray-50 dark:hover:bg-white/5 transition"
                  onClick={() => { setSupportPreset({ slug: "appeal", subject: `Appeal: ${mediaName}`, message: `I'd like to appeal the decision on my upload "${mediaName}".\n\nI believe it should be validated because: ` }); setSupportOpen(true); }}
                >
                  Think this is an error? Report and our team will review.
                </button>
              )}
            </>
          );
        };

        const handleCloseDetails = () => {
          setDetailsFile(null);
          setHasCheckedVoiceover(false);
          setVoiceoverStatusData(null);
        };

        return (
          <div
            className="fixed inset-0 z-50 flex items-start sm:items-center justify-center bg-black/40 backdrop-blur-sm p-4 sm:p-6 overflow-y-auto [&::-webkit-scrollbar]:hidden"
            style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
            onClick={handleCloseDetails}
          >
            <div
              className="w-full max-w-5xl flex flex-col rounded-xl bg-white dark:bg-[#0a0a0a] text-black dark:text-white border border-black dark:border-[#8F8F8F] shadow-2xl overflow-hidden my-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between px-5 md:px-6 py-4 shrink-0 bg-white dark:bg-[#0a0a0a] border-b border-black dark:border-[#8F8F8F]">
                <div className="min-w-0 pr-3">
                  <div className="text-sm md:text-base font-semibold truncate tracking-wide font-['Product_Sans']">
                    {fileName}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 truncate font-['Product_Sans']">{f.name || f.originalFilename}</div>
                </div>
                <button
                  className="text-gray-500 hover:text-black dark:hover:text-white transition shrink-0"
                  onClick={handleCloseDetails}
                  aria-label="Close"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                </button>
              </div>

              <div className="flex flex-col md:flex-row flex-1 overflow-y-auto md:overflow-hidden bg-white dark:bg-[#0a0a0a] [&::-webkit-scrollbar]:hidden"
                style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}>
                {/* Left Column (Image & Actions Desktop) - Matching MediaDetailsModal completely */}
                <div className="w-full md:w-[45%] p-5 md:p-6 flex flex-col border-b md:border-b-0 md:border-r border-black dark:border-[#8F8F8F] shrink-0 md:overflow-y-auto [&::-webkit-scrollbar]:hidden"
                  style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}>
                  
                  <div className="w-full flex items-center justify-center bg-transparent mb-4 relative">
                    {f.type?.startsWith("image/") && f.url ? (
                      <img
                        src={f.url}
                        alt={fileName}
                        className="w-full h-auto max-h-[40vh] md:max-h-[50vh] object-contain shadow-sm border border-black dark:border-[#8F8F8F] rounded"
                        onError={(e) => {
                          (e.currentTarget as HTMLImageElement).src =
                            "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100' viewBox='0 0 100 100'%3E%3Crect width='100' height='100' fill='%23222'/%3E%3Ctext x='50' y='50' font-family='Arial' font-size='14' fill='%23fff' text-anchor='middle' dy='.3em'%3EImage%3C/text%3E%3C/svg%3E";
                        }}
                      />
                    ) : f.type?.startsWith("video/") && f.url ? (
                      <div className="w-full flex flex-col items-center justify-center">
                        <video
                          src={f.url.includes("#") ? f.url : `${f.url}#t=0.001`}
                          controls
                          preload="metadata"
                          className="w-full h-auto max-h-[40vh] md:max-h-[50vh] rounded shadow-sm border border-black dark:border-[#8F8F8F] bg-black/5 dark:bg-black/20"
                          poster={f.thumbnailUrl || undefined}
                        />
                        <div className="text-xs text-gray-500 dark:text-gray-400 text-center mt-2 font-['Product_Sans']">
                          Video player controls available
                        </div>
                      </div>
                    ) : (
                      <div className="w-full h-48 flex items-center justify-center border border-black dark:border-[#8F8F8F] rounded bg-gray-50 dark:bg-white/5">
                        <span className="text-4xl text-gray-400">📄</span>
                      </div>
                    )}
                  </div>

                  <button
                    className="w-full py-3 bg-black text-white dark:bg-white dark:text-black rounded uppercase text-[11px] font-bold tracking-[0.15em] hover:opacity-90 transition mb-6 shrink-0"
                    onClick={() => openFile(f.id)}
                    disabled={!f.url}
                  >
                    Open Full Preview
                  </button>

                  {/* Desktop Actions pushed to bottom */}
                  <div className="hidden md:flex flex-col space-y-3 mt-auto pt-4">
                    {renderActionButtons()}
                  </div>
                </div>

                {/* Right Column (Details) */}
                <div className="w-full md:w-[55%] p-5 md:p-6 flex flex-col space-y-6 md:overflow-y-auto [&::-webkit-scrollbar]:hidden"
                  style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}>

                  {/* Status Grid */}
                  <div className="grid grid-cols-3 gap-2">
                    <div className="flex flex-col">
                      <span className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-gray-400 font-bold mb-1.5 font-['Product_Sans']">Status</span>
                      {f.status ? (
                        <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 border border-black dark:border-[#8F8F8F] text-[10px] uppercase tracking-wider font-bold rounded w-max ${f.status === "CONFIRMED" || f.status === "COMPLETED" ? "text-emerald-500" : f.status === "FAILED" ? "text-red-500" : "text-yellow-500"}`}>
                          {f.status === "FAILED" && <span className="w-1.5 h-1.5 rounded-full bg-red-500"></span>}
                          {f.status}
                        </span>
                      ) : (
                        <span className="text-[11px] text-gray-400">—</span>
                      )}
                    </div>
                    <div className="flex flex-col">
                      <span className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-gray-400 font-bold mb-1.5 font-['Product_Sans']">Uploaded</span>
                      <span className="text-[11px] text-black dark:text-white font-medium">{formatDateUK(f.createdAt)}</span>
                    </div>
                    <div className="flex flex-col">
                      <span className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-gray-400 font-bold mb-1.5 font-['Product_Sans']">Size</span>
                      <span className="text-[11px] text-black dark:text-white font-medium">{formatBytes(f.size || Number(f.fileSize || 0))}</span>
                    </div>
                  </div>

                  <hr className="border-black dark:border-[#8F8F8F]" />

                  {/* Technical Identifiers */}
                  {(() => {
                    const ownerKey = f.ownerKey ?? f.meta?.ownerKey;
                    const nftToken = f.nftToken ?? f.nftTokenId ?? f.meta?.nftTokenId;
                    const mediaId = f.mediaId ?? f.meta?.mediaId ?? f.id;

                    return (
                      <div>
                        <div className="text-[11px] uppercase tracking-widest text-black dark:text-white font-bold mb-4 font-['Product_Sans']">Technical Identifiers</div>
                        <div className="space-y-3">
                          <div className="flex items-center justify-between text-[11px] font-['Product_Sans'] gap-2">
                            <span className="text-gray-500 dark:text-gray-400 uppercase tracking-wider shrink-0">NFT Token</span>
                            <span className="text-black dark:text-gray-300 font-medium truncate max-w-[150px] sm:max-w-[200px] text-right">{nftToken || "—"}</span>
                          </div>
                          <hr className="border-black dark:border-[#8F8F8F]" />

                          <div className="flex items-center justify-between text-[11px] font-['Product_Sans'] gap-2">
                            <span className="text-gray-500 dark:text-gray-400 uppercase tracking-wider shrink-0">Vastavik ID</span>
                            <span className="text-black dark:text-gray-300 font-medium truncate max-w-[150px] sm:max-w-[200px] text-right">{ownerKey || "—"}</span>
                          </div>
                          <hr className="border-black dark:border-[#8F8F8F]" />

                          <div className="flex items-center justify-between text-[11px] font-['Product_Sans'] gap-2">
                            <span className="text-gray-500 dark:text-gray-400 uppercase tracking-wider shrink-0">Media ID</span>
                            <span className="bg-gray-100 dark:bg-[#222] text-black dark:text-gray-300 px-2 py-1 rounded truncate max-w-[150px] sm:max-w-[250px] text-right">{mediaId}</span>
                          </div>
                          <hr className="border-black dark:border-[#8F8F8F]" />

                          <div className="flex items-center justify-between text-[11px] font-['Product_Sans'] gap-2">
                            <span className="text-gray-500 dark:text-gray-400 uppercase tracking-wider shrink-0">Type</span>
                            <span className="text-black dark:text-gray-300 font-medium text-right">{f.type || f.mimeType || "—"}</span>
                          </div>

                          {f.type?.startsWith("image/") && f.dimensions?.width && f.dimensions?.height && (
                            <>
                              <hr className="border-black dark:border-[#8F8F8F]" />
                              <div className="flex items-center justify-between text-[11px] font-['Product_Sans'] gap-2">
                                <span className="text-gray-500 dark:text-gray-400 uppercase tracking-wider shrink-0">Dimensions</span>
                                <span className="text-black dark:text-gray-300 font-medium text-right">{f.dimensions.width} × {f.dimensions.height} px</span>
                              </div>
                            </>
                          )}

                          {(f.isAudio || f.type?.startsWith("audio/")) && f.audioMetadata && (
                             <>
                              {f.audioMetadata.duration && (
                                <>
                                  <hr className="border-black dark:border-[#8F8F8F]" />
                                  <div className="flex items-center justify-between text-[11px] font-['Product_Sans'] gap-2">
                                    <span className="text-gray-500 dark:text-gray-400 uppercase tracking-wider shrink-0">Duration</span>
                                    <span className="text-black dark:text-gray-300 font-medium text-right">{formatDuration(f.audioMetadata.duration)}</span>
                                  </div>
                                </>
                              )}
                              {f.audioMetadata.bitrate && (
                                 <>
                                   <hr className="border-black dark:border-[#8F8F8F]" />
                                   <div className="flex items-center justify-between text-[11px] font-['Product_Sans'] gap-2">
                                     <span className="text-gray-500 dark:text-gray-400 uppercase tracking-wider shrink-0">Bitrate</span>
                                     <span className="text-black dark:text-gray-300 font-medium text-right">{Math.round(f.audioMetadata.bitrate / 1000)} kbps</span>
                                   </div>
                                 </>
                              )}
                             </>
                          )}
                        </div>
                      </div>
                    );
                  })()}

                  <hr className="border-black dark:border-[#8F8F8F]" />

                  {/* Verification & Protection */}
                  {(() => {
                      const ownerKey = f.ownerKey ?? f.meta?.ownerKey;
                      const verifyUrl = f.verificationUrl ?? f.meta?.verificationUrl ?? (ownerKey ? `https://vastavik.org/verify/${ownerKey}` : null);
                      const protectionType = f.protectionType ?? f.meta?.protectionType;
                      const protectionLabel = protectionType
                        ? ({ qr: "QR Code", qr_watermark: "QR Code", vastavik_id: "Vastavik ID", ownerkey: "Vastavik ID", voiceover: "Voiceover (Voice ID)" } as any)[String(protectionType).toLowerCase()]
                          ?? (String(protectionType).charAt(0).toUpperCase() + String(protectionType).slice(1))
                        : null;
                      const txId = f.blockchainTxId ?? f.txId ?? f.meta?.blockchainTxId;
                      const phase = getUploadPhase(f);
                      const blockchainLabel = phase.phase === "failed" ? "REJECTED" : phase.phase === "verifying" ? "VERIFYING" : phase.phase === "processing" ? "PENDING" : "REGISTERED";
                      const blockchainColor = phase.phase === "failed" ? "border-black dark:border-[#8F8F8F] text-red-500" : phase.phase === "verifying" ? "border-black dark:border-[#8F8F8F] text-emerald-500" : phase.phase === "processing" ? "border-black dark:border-[#8F8F8F] text-yellow-500" : "border-black dark:border-[#8F8F8F] text-emerald-500";

                      const rejectionReason = f.rejectionReason ?? f.meta?.rejectionReason;
                      const backendCanVerify = f.canVerifyWithOriginal ?? f.meta?.canVerifyWithOriginal;
                      const rejectionCode = String(f.rejectionCode ?? f.meta?.rejectionCode ?? "").toUpperCase();
                      const TERMINAL_REJECTION_CODES = ["SCREENSHOT", "SCREEN RECORDING", "DEEPFAKE", "DEEPFAKE_CHECK_UNAVAILABLE", "DEEPFAKE_UNSUPPORTED_FORMAT", "DEEPFAKE_CHECK_FAILED", "DUPLICATE", "ALREADY_REGISTERED", "ALREADY REGISTERED"];
                      const terminalRejection = backendCanVerify != null ? !backendCanVerify : rejectionCode ? TERMINAL_REJECTION_CODES.includes(rejectionCode) : /screenshot|screen recording|deepfake|duplicate|uploaded before|already been|already registered/i.test(String(rejectionReason || ""));

                      return (
                        <div>
                          <div className="text-[11px] uppercase tracking-widest text-black dark:text-white font-bold mb-4 font-['Product_Sans']">Verification & Protection</div>
                          <div className="space-y-4">

                            {phase.phase === "failed" && !terminalRejection && !f.matchVerification &&
                              /^(image|video|audio)\//.test(f.type || "") && (
                              <div className="flex items-center gap-2 rounded-lg border border-black dark:border-[#8F8F8F] bg-emerald-500/10 px-3 py-2 text-[11px] text-emerald-700 dark:text-emerald-200/90 font-['Product_Sans']">
                                <span className="text-sm leading-none animate-bounce">↓</span>
                                <span>You can still validate this — scroll down to upload your original.</span>
                              </div>
                            )}

                            {phase.phase === "verifying" && (
                              <div className="flex items-start gap-2 rounded-lg border border-black dark:border-[#8F8F8F] bg-emerald-500/10 px-3 py-2 text-[11px] text-emerald-700 dark:text-emerald-200/90 font-['Product_Sans']">
                                <span className="mt-0.5 inline-block h-3 w-3 shrink-0 animate-spin rounded-full border-2 border-black dark:border-[#8F8F8F] border-t-emerald-500" />
                                <span>Verifying your original against this media… Your library will update automatically once it’s done.</span>
                              </div>
                            )}

                            <div className="flex items-center justify-between text-[11px] font-['Product_Sans'] gap-2">
                              <span className="text-gray-500 dark:text-gray-400 uppercase tracking-wider shrink-0">Blockchain</span>
                              <span className={`px-2 py-0.5 border text-[9px] uppercase tracking-widest font-bold rounded ${blockchainColor}`}>{blockchainLabel}</span>
                            </div>

                            {protectionLabel && (
                              <div className="flex items-center justify-between text-[11px] font-['Product_Sans'] gap-2">
                                <span className="text-gray-500 dark:text-gray-400 uppercase tracking-wider shrink-0">Protection</span>
                                <span className="text-black dark:text-gray-300 font-medium text-right">{protectionLabel}</span>
                              </div>
                            )}

                            {verifyUrl && (
                              <div className="flex items-center justify-between text-[11px] font-['Product_Sans'] gap-2">
                                <span className="text-gray-500 dark:text-gray-400 uppercase tracking-wider shrink-0">Verify URL</span>
                                <a href={verifyUrl} target="_blank" rel="noreferrer" className="text-blue-500 dark:text-blue-400 hover:underline max-w-[150px] sm:max-w-[200px] truncate text-right">{verifyUrl}</a>
                              </div>
                            )}

                            {txId && (
                              <div className="flex items-center justify-between text-[11px] font-['Product_Sans'] gap-2">
                                <span className="text-gray-500 dark:text-gray-400 uppercase tracking-wider shrink-0">Transaction ID</span>
                                <span className="text-black dark:text-gray-300 max-w-[150px] sm:max-w-[200px] truncate text-right">{txId}</span>
                              </div>
                            )}
                          </div>

                          {phase.phase === "failed" && !terminalRejection && (
                            <div className="mt-5 border border-black dark:border-[#8F8F8F] rounded-lg p-4 relative bg-red-50/50 dark:bg-transparent">
                              <div className="absolute left-0 top-0 bottom-0 w-1 bg-red-500 rounded-l-lg"></div>
                              <div className="flex items-center gap-2 mb-3 text-red-500 text-[10px] uppercase font-bold tracking-widest font-['Product_Sans']">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12.01" y2="16"></line></svg>
                                {VALIDATION_FAILED_TITLE}
                              </div>
                              <ol className="list-decimal pl-4 space-y-1.5 text-[11px] text-gray-700 dark:text-gray-300 font-['Product_Sans'] leading-relaxed">
                                {VALIDATION_FAILED_REASONS.map((r) => (<li key={r}>{r}</li>))}
                              </ol>
                              <div className="text-[11px] text-gray-700 dark:text-gray-300 mt-3 font-['Product_Sans'] leading-relaxed">{VALIDATION_FAILED_HELP}</div>
                            </div>
                          )}

                          {phase.phase === "failed" && terminalRejection && rejectionReason && (
                            <div className="mt-5 border border-black dark:border-[#8F8F8F] rounded-lg p-4 relative bg-red-50/50 dark:bg-transparent">
                              <div className="absolute left-0 top-0 bottom-0 w-1 bg-red-500 rounded-l-lg"></div>
                              <div className="flex items-center gap-2 mb-2 text-red-500 text-[10px] uppercase font-bold tracking-widest font-['Product_Sans']">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12.01" y2="16"></line></svg>
                                Reason
                              </div>
                              <div className="text-[11px] text-gray-700 dark:text-gray-300 font-['Product_Sans'] leading-relaxed">{rejectionReason}</div>
                            </div>
                          )}
                        </div>
                      );
                  })()}

                  {/* Voiceover (audio) */}
                  {(f.isAudio || f.type?.startsWith("audio/")) && (
                    <>
                      <hr className="border-black dark:border-[#8F8F8F]" />
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <div className="text-[11px] uppercase tracking-widest text-black dark:text-white font-bold font-['Product_Sans']">Voiceover</div>
                          <div className="flex gap-2">
                            {voiceoverStatusLoading || isRefreshingVoiceover ? (
                              <button disabled className="rounded border border-black dark:border-[#8F8F8F] bg-transparent px-2 py-1 text-[10px] uppercase tracking-wider text-gray-500 flex items-center"><Spinner className="h-3 w-3 mr-1 text-gray-400" /> Loading...</button>
                            ) : f.voiceoverGenerated && f.voiceoverUrl ? (
                              <button onClick={handleShareVoiceover} className="rounded border border-black dark:border-[#8F8F8F] bg-transparent px-2 py-1 text-[10px] uppercase tracking-wider text-black dark:text-white hover:bg-gray-50 dark:hover:bg-white/10 transition">Share</button>
                            ) : (
                              <button onClick={handleCheckVoiceoverStatus} className="rounded border border-black dark:border-[#8F8F8F] bg-transparent px-2 py-1 text-[10px] uppercase tracking-wider text-black dark:text-white hover:bg-gray-50 dark:hover:bg-white/10 transition">Refresh</button>
                            )}
                          </div>
                        </div>
                        {!voiceoverStatusLoading && f.voiceoverPending && (<div className="flex justify-between text-[11px] font-['Product_Sans']"><span className="text-gray-500 uppercase tracking-wider">Status</span><span className="text-yellow-600 dark:text-yellow-400 font-medium">⏳ Being generated</span></div>)}
                        {!voiceoverStatusLoading && f.voiceoverGenerated && f.voiceoverUrl && (
                          <div className="space-y-2 mt-2">
                            <div className="text-[11px] text-gray-500 dark:text-gray-400 uppercase tracking-wider font-['Product_Sans']">Vastavik Voice ID</div>
                            <audio controls className="w-full h-9"><source src={f.voiceoverUrl} type="audio/mpeg" /></audio>
                          </div>
                        )}
                        {!voiceoverStatusLoading && !f.voiceoverGenerated && !f.voiceoverPending && (<div className="flex justify-between text-[11px] font-['Product_Sans']"><span className="text-gray-500 uppercase tracking-wider">Status</span><span className="text-gray-400">No voiceover generated yet</span></div>)}
                      </div>
                    </>
                  )}

                  {/* Mobile Actions (Hidden on Desktop) */}
                  <div className="flex md:hidden flex-col space-y-3 mt-6 pt-6 border-t border-black dark:border-[#8F8F8F]">
                    {renderActionButtons()}
                  </div>

                </div>
              </div>

              {/* Footer */}
              <div className="flex flex-wrap justify-end gap-2 sm:gap-3 border-t border-black dark:border-[#8F8F8F] px-5 md:px-6 py-4 shrink-0 bg-white dark:bg-[#0a0a0a]">
                {hasVastavikId && (
                  isQrProtected ? (
                    <button
                      className="py-2 px-4 rounded-md border border-black dark:border-[#8F8F8F] text-black dark:text-white hover:bg-gray-50 dark:hover:bg-white/10 transition disabled:opacity-40 text-[12px] sm:text-[13px] font-['Product_Sans']"
                      onClick={(e) => downloadAssetLocal("qr", e)}
                    >
                      Download Vastavik QR
                    </button>
                  ) : (
                    <button
                      className="py-2 px-4 rounded-md border border-black dark:border-[#8F8F8F] text-black dark:text-white hover:bg-gray-50 dark:hover:bg-white/10 transition disabled:opacity-40 text-[12px] sm:text-[13px] font-['Product_Sans']"
                      onClick={(e) => downloadAssetLocal("badge", e)}
                    >
                      Download Vastavik ID Badge
                    </button>
                  )
                )}
                {getUploadPhase(f).phase !== "failed" && (
                  <button
                    className="py-2 px-5 rounded-md border border-black dark:border-[#8F8F8F] text-black dark:text-white hover:bg-gray-50 dark:hover:bg-white/10 transition disabled:opacity-40 text-[12px] sm:text-[13px] font-['Product_Sans']"
                    onClick={() => downloadFile(f.id)}
                    disabled={!f.url || downloading}
                  >
                    {downloading ? "Downloading…" : "Download"}
                  </button>
                )}
                <button
                  className="py-2 px-5 rounded-md bg-black text-white dark:bg-white dark:text-black hover:opacity-90 transition disabled:opacity-40 text-[11px] sm:text-[12px] font-bold uppercase tracking-[0.15em]"
                  onClick={() => openFile(f.id)}
                  disabled={!f.url}
                >
                  Open
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {verifyOpen && detailsFile && (
        <VerifyOriginalModal
          mediaId={String((detailsFile as any).mediaId ?? (detailsFile as any).meta?.mediaId ?? detailsFile.id)}
          mediaName={detailsFile.displayName || detailsFile.name}
          mediaKind={detailsFile.type?.startsWith("video/") ? "video" : detailsFile.type?.startsWith("audio/") ? "audio" : "image"}
          startNoOriginal={verifyNoOriginal}
          onClose={() => setVerifyOpen(false)}
          onGoToLibrary={() => {
            setVerifyOpen(false);
            setDetailsFile(null);
            try { window.dispatchEvent(new Event("vastavik:upload-completed")); } catch { /* noop */ }
          }}
          onVerified={() => {
            try { window.dispatchEvent(new Event("vastavik:upload-completed")); } catch { /* noop */ }
          }}
        />
      )}

      {supportOpen && detailsFile && (
        <SupportTicketModal
          open={supportOpen}
          onClose={() => setSupportOpen(false)}
          presetCategorySlug={supportPreset?.slug}
          presetSubject={supportPreset?.subject}
          presetMessage={supportPreset?.message}
          relatedMediaId={String((detailsFile as any).mediaId ?? (detailsFile as any).meta?.mediaId ?? detailsFile.id)}
        />
      )}

      {folderModal && (
        <FolderNameModal
          open={!!folderModal}
          mode={folderModal.mode}
          title={folderModal.title}
          initialValue={folderModal.initialValue}
          confirmText={folderModal.mode === "create" ? "Create" : folderModal.mode === "rename" ? "Rename" : undefined}
          onClose={() => setFolderModal(null)}
          onConfirm={async (value: string) => {
            const ctx = folderModal.context;
            if (ctx.type === "rename-file-label") {
              setFiles((prev) => prev.map((f) => (f.id === ctx.fileId ? { ...f, displayName: value } : f)));
              return;
            }
            if (ctx.type === "rename-folder") {
              if (onRenameFolder && (onRenameFolder as any).length > 0) {
                return await Promise.resolve((onRenameFolder as (name: string) => any)(value));
              }
              return;
            }
            if (ctx.type === "create-subfolder") {
              if (onRenameFolder && (onRenameFolder as any).length > 0) {
                return await Promise.resolve((onRenameFolder as (name: string) => any)(value));
              }
              return;
            }
          }}
        />
      )}
    </div>
  );
}